import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  AppCard,
  AppInput,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
} from "@/layout/AppComponents";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import styled from "styled-components";
import LazyLoad from "react-lazyload";
import ReactDOMServer from "react-dom/server";
import Translator from "../../../../layout/components/helpers/Translator";
import { axiosPath } from "../../../../routing/helpers/router";
import BreadcrumbHandler from "../../../../layout/components/breadcrumb/BreadcrumbHandler";
import CommunicationPipelineAdminNavbar from "../../../../layout/screenSpecifics/Modules/CommunicationPipeline/Admin/CommunicationPipelineAdminNavbar";
import AppButton from "../../../../layout/components/form/AppButton";
import minions from "../../../../minions/minions";
import AppLink from "../../../../routing/helpers/AppLink";
import colors from "../../../../constants/colors";
import useIsMounted from "../../../../layout/components/hooks/useIsMounted";
import errorHandler from "@/layout/components/error/ErrorHandler";
import CommunicationPipelineModuleSidebar from "@/layout/screenSpecifics/Modules/CommunicationPipeline/CommunicationPipelineModuleSidebar";
import * as currentPageAction from "@/store/actions/currentPageAction";

export default function CommunicationPipelineAdminReportThreadScreen() {
  const { moduleBridgeHex, reportHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [userType] = useState("admin");
  const [threads, setThreads] = useState();
  const [unseenCommunicationThreads, setUnseenCommunicationThreads] = useState(
    [],
  );
  const [selectedThread, setSelectedThread] = useState();
  const [showAddModal, setShowAddModal] = useState(false);
  const [validationSchema] = useState(
    Yup.object().shape({
      name: Yup.string().required(
        ReactDOMServer.renderToString(<Translator file="module-communicationPipeline" name="required" />),
      ),
    }),
  );

  const loadThreads = useCallback(async () => {
    axios
      .post(
        axiosPath("api_r_user_module_communicationPipeline_report_thread_list"),
        {
          moduleBridgeHex,
          reportHex,
          type: userType,
        },
      )
      .then((r) => {
        if (isMounted.current) {
          setThreads(r?.data?.threads);
          setUnseenCommunicationThreads(r?.data?.unseenCommunicationThreads);
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, reportHex, userType]);

  useEffect(() => {
    loadThreads();
  }, []);

  const handleAddThread = useCallback(
    async (payload) => {
      axios
        .post(
          axiosPath(
            "api_r_user_module_communicationPipeline_report_thread_add",
          ),
          minions.forms.toFormData({ ...payload, moduleBridgeHex, reportHex }),
          { headers: { "Content-Type": "multipart/form-data" } },
        )
        .then((r) => {
          if (isMounted.current && r?.data?.thread) {
            setThreads([r?.data?.thread, ...threads]);
            setShowAddModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, reportHex, threads],
  );

    // > LOAD MODULE DATA //
  useEffect(() => {
    axios
      .post(
        axiosPath("api_r_user_module_communicationPipeline_getModuleData"),
        { moduleBridgeHex },
      )
      .then((r) => {
        dispatch(currentPageAction.change("moduleData", r.data, true));
      });
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <>
      {moduleData?.permissionLevel === 0 && (
        <CommunicationPipelineModuleSidebar />
      )}
      <Container>
        <BreadcrumbHandler
          paths={
            moduleBridgeHex
              ? [
                  {
                    name: `${moduleData?.moduleName}`,
                    route: {
                      name: "user_module_communicationPipeline_home",
                      params: {
                        moduleBridgeHex,
                      },
                    },
                  },
                  {
                    name: `${ReactDOMServer.renderToString(
                      <Translator
                        file="module-communicationPipeline"
                        name="complaint"
                      />,
                    )} - ${reportHex}`,
                  },
                ]
              : [{ name: "..." }]
          }
        />
        <CommunicationPipelineAdminNavbar />
        <AppButton
          color="success"
          className="float-right mb-3"
          icon="add"
          onClick={() => setShowAddModal(true)}
        >
          <Translator
            file="module-communicationPipeline"
            name="create-thread"
          />
        </AppButton>
        <div className="clearfix" />
        {threads &&
          threads.map((t) => (
            <LazyLoad key={t.hex}>
              <AppCard className="mb-2">
                <div className="d-flex align-center">
                  <AppLink
                    path="user_module_communicationPipeline_admin_report_communication"
                    params={{
                      moduleBridgeHex,
                      reportHex,
                      threadHex: t.hex,
                    }}
                    className="wl-title-md wl-title-color"
                  >
                    <span className="badge badge-dark mr-2">
                      {t?.dateStamp}
                    </span>
                    <span className="font-weight-bold">{t?.name}</span>
                    {unseenCommunicationThreads?.includes(t?.hex) && (
                      <span className="ml-2">
                        <AppRemixIcon
                          icon="notification"
                          color={colors.bootstrapWarning}
                          filled
                        />
                      </span>
                    )}
                  </AppLink>
                </div>
              </AppCard>
            </LazyLoad>
          ))}
        <AppModal
          title={`${(
            <Translator file="module-communicationPipeline" name="thread" />
          )} - ${selectedThread?.hex}`}
          show={!!selectedThread}
          onClose={() => setSelectedThread(undefined)}
          onConfirm={() => setSelectedThread(undefined)}
          confirmBtnText={
            <Translator file="module-communicationPipeline" name="close" />
          }
          isConfirmBtn
        >
          <AppInput
            title={ReactDOMServer.renderToString(
              <Translator file="module-communicationPipeline" name="name" />,
            )}
            value={selectedThread?.name}
            isReadOnly
          />
        </AppModal>
        <AppModal
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-communicationPipeline"
              name="create-thread"
            />,
          )}
          show={showAddModal}
          onClose={() => setShowAddModal(false)}
        >
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={{
              name: "",
            }}
            onSubmit={handleAddThread}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <AppInput
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-communicationPipeline"
                      name="name"
                    />,
                  )}
                  value={values?.name}
                  error={errors?.name}
                  onChange={(v) => setFieldValue("name", v)}
                />
                <button
                  type="submit"
                  className="btn btn-success btn-sm float-right"
                  disabled={isSubmitting}
                >
                  <Translator file="module-communicationPipeline" name="add" />
                </button>
              </form>
            )}
          </Formik>
        </AppModal>
      </Container>
    </>
  );
}

const Container = styled.div`
  padding-left: 0.5rem !important;
`;
